import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { API_CONFIG } from '../config/firebase';

interface LogEntry {
  id: string;
  timestamp: any;
  level: 'info' | 'warn' | 'error' | 'debug';
  message: string;
  endpoint?: string;
  method?: string;
  statusCode?: number;
  userAgent?: string;
  ip?: string;
  userId?: string;
  username?: string;
  duration?: number;
  error?: string;
  stack?: string;
  // Additional fields
  action?: string;
  licenseKey?: string;
  hardwareFingerprint?: string;
  machineName?: string;
  requestData?: any;
  responseData?: any;
  pluginVersion?: string;
  revitVersion?: string;
}

interface LogsResponse {
  success: boolean;
  data: LogEntry[];
  pagination: {
    page: number;
    limit: number;
    hasMore: boolean;
  };
}

export function Logs() {
  const { currentUser } = useAuth();
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showClearModal, setShowClearModal] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [expandedLogs, setExpandedLogs] = useState<Set<string>>(new Set());
  
  const [filters, setFilters] = useState({
    search: '',
    level: '',
    endpoint: '',
    timeRange: '24h' // new filter
  });

  const showMessage = (type: 'success' | 'error', text: string) => {
    setMessage({ type, text });
    setTimeout(() => setMessage(null), 5000);
  };

  const toggleLogExpansion = (logId: string) => {
    setExpandedLogs(prev => {
      const newSet = new Set(prev);
      if (newSet.has(logId)) {
        newSet.delete(logId);
      } else {
        newSet.add(logId);
      }
      return newSet;
    });
  };

  const getAuthHeaders = async () => {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json'
    };
    
    if (currentUser) {
      try {
        const token = await currentUser.getIdToken();
        headers['Authorization'] = `Bearer ${token}`;
      } catch (error) {
        console.error('Failed to get ID token:', error);
      }
    }
    
    return headers;
  };

  const fetchLogs = async () => {
    try {
      setLoading(true);
      setError(null);

      const headers = await getAuthHeaders();
      const response = await fetch(`${API_CONFIG.baseURL}${API_CONFIG.endpoints.admin}/logs?page=1&limit=100`, {
        headers
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data: LogsResponse = await response.json();

      if (data.success) {
        // Sort logs by timestamp (newest first)
        const sortedLogs = data.data.sort((a, b) => {
          const dateA = getTimestampAsDate(a.timestamp);
          const dateB = getTimestampAsDate(b.timestamp);
          return dateB.getTime() - dateA.getTime(); // Newest first
        });
        setLogs(sortedLogs);
      } else {
        throw new Error('Failed to fetch logs');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch logs');
      console.error('Error fetching logs:', err);
    } finally {
      setLoading(false);
    }
  };

  const clearLogs = async () => {
    setClearing(true);
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_CONFIG.baseURL}${API_CONFIG.endpoints.admin}/logs/clear`, {
        method: 'DELETE',
        headers
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      if (data.success) {
        setShowClearModal(false);
        await fetchLogs();
        showMessage('success', 'Logs succesvol gewist');
      } else {
        throw new Error(data.error || 'Failed to clear logs');
      }
    } catch (err) {
      showMessage('error', 'Fout bij wissen van logs: ' + (err instanceof Error ? err.message : 'Onbekende fout'));
    } finally {
      setClearing(false);
    }
  };

  useEffect(() => {
    fetchLogs();
  }, [currentUser]);

  const getTimestampAsDate = (timestamp: any): Date => {
    if (!timestamp) return new Date(0);
    
    try {
      // Same logic as formatDate
      if (timestamp._seconds !== undefined) {
        return new Date(timestamp._seconds * 1000);
      } else if (timestamp.seconds !== undefined) {
        return new Date(timestamp.seconds * 1000);
      } else if (timestamp && typeof timestamp === 'object' && timestamp.toMillis) {
        return new Date(timestamp.toMillis());
      } else if (timestamp && typeof timestamp === 'object' && timestamp.toDate) {
        return timestamp.toDate();
      } else if (typeof timestamp === 'number') {
        if (timestamp < 10000000000) {
          return new Date(timestamp * 1000);
        } else {
          return new Date(timestamp);
        }
      } else if (typeof timestamp === 'string') {
        return new Date(timestamp);
      } else if (timestamp instanceof Date) {
        return timestamp;
      }
      return new Date(0);
    } catch (error) {
      return new Date(0);
    }
  };

  const formatDate = (timestamp: any) => {
    if (!timestamp) return 'Onbekend';
    
    try {
      let date: Date;
      
      // Handle various timestamp formats
      if (timestamp._seconds !== undefined) {
        date = new Date(timestamp._seconds * 1000);
      } else if (timestamp.seconds !== undefined) {
        date = new Date(timestamp.seconds * 1000);
      } else if (timestamp && typeof timestamp === 'object' && timestamp.toMillis) {
        // Firestore Timestamp object with toMillis method
        date = new Date(timestamp.toMillis());
      } else if (timestamp && typeof timestamp === 'object' && timestamp.toDate) {
        // Firestore Timestamp object with toDate method
        date = timestamp.toDate();
      } else if (typeof timestamp === 'number') {
        // Unix timestamp (could be seconds or milliseconds)
        if (timestamp < 10000000000) {
          // Likely seconds
          date = new Date(timestamp * 1000);
        } else {
          // Likely milliseconds
          date = new Date(timestamp);
        }
      } else if (typeof timestamp === 'string') {
        // ISO string or other date string
        date = new Date(timestamp);
      } else if (timestamp instanceof Date) {
        date = timestamp;
      } else {
        console.error('Unknown timestamp format:', timestamp);
        return 'Onbekend';
      }
      
      if (isNaN(date.getTime())) {
        console.error('Invalid date:', date);
        return 'Ongeldig';
      }
      
      // Use toLocaleString instead of toLocaleDateString to include time
      return date.toLocaleString('nl-NL', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      });
    } catch (error) {
      console.error('Error formatting date:', error, timestamp);
      return 'Ongeldig';
    }
  };

  const getRelativeTime = (timestamp: any) => {
    if (!timestamp) return '';
    
    try {
      let date: Date;
      
      // Same logic as formatDate for consistency
      if (timestamp._seconds !== undefined) {
        date = new Date(timestamp._seconds * 1000);
      } else if (timestamp.seconds !== undefined) {
        date = new Date(timestamp.seconds * 1000);
      } else if (timestamp && typeof timestamp === 'object' && timestamp.toMillis) {
        date = new Date(timestamp.toMillis());
      } else if (timestamp && typeof timestamp === 'object' && timestamp.toDate) {
        date = timestamp.toDate();
      } else if (typeof timestamp === 'number') {
        if (timestamp < 10000000000) {
          date = new Date(timestamp * 1000);
        } else {
          date = new Date(timestamp);
        }
      } else if (typeof timestamp === 'string') {
        date = new Date(timestamp);
      } else {
        return '';
      }
      
      if (isNaN(date.getTime())) {
        return '';
      }
      
      const now = new Date();
      const diffMs = now.getTime() - date.getTime();
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMs / 3600000);
      const diffDays = Math.floor(diffMs / 86400000);
      
      if (diffMins < 1) return 'Zojuist';
      if (diffMins < 60) return `${diffMins} minuten geleden`;
      if (diffHours < 24) return `${diffHours} uur geleden`;
      if (diffDays < 7) return `${diffDays} dagen geleden`;
      return '';
    } catch (error) {
      return '';
    }
  };

  const getLevelBadge = (level: string | undefined) => {
    if (!level) {
      return (
        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-800">
          <span className="w-2 h-2 bg-gray-500 rounded-full mr-2"></span>
          LOG
        </span>
      );
    }
    
    switch (level.toLowerCase()) {
      case 'error':
        return (
          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200">
            <span className="w-2 h-2 bg-red-500 rounded-full mr-2 animate-pulse"></span>
            ERROR
          </span>
        );
      case 'warn':
      case 'warning':
        return (
          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200">
            <span className="w-2 h-2 bg-yellow-500 rounded-full mr-2"></span>
            WARN
          </span>
        );
      case 'info':
        return (
          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
            <span className="w-2 h-2 bg-blue-500 rounded-full mr-2"></span>
            INFO
          </span>
        );
      case 'debug':
        return (
          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200">
            <span className="w-2 h-2 bg-gray-500 rounded-full mr-2"></span>
            DEBUG
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-800">
            <span className="w-2 h-2 bg-gray-500 rounded-full mr-2"></span>
            {level ? level.toUpperCase() : 'LOG'}
          </span>
        );
    }
  };

  const getActionBadge = (action?: string) => {
    if (!action) return null;
    
    const actionColors: Record<string, string> = {
      'activate': 'bg-green-100 text-green-800',
      'validate': 'bg-blue-100 text-blue-800',
      'deactivate': 'bg-orange-100 text-orange-800',
      'request_reset': 'bg-purple-100 text-purple-800',
      'reset_password': 'bg-indigo-100 text-indigo-800'
    };
    
    const colorClass = actionColors[action] || 'bg-gray-100 text-gray-800';
    
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${colorClass}`}>
        {action.replace(/_/g, ' ').toUpperCase()}
      </span>
    );
  };

  const getStatusCodeBadge = (statusCode?: number) => {
    if (!statusCode) return null;
    
    let colorClass = 'bg-gray-100 text-gray-800';
    let label = '';
    
    if (statusCode >= 200 && statusCode < 300) {
      colorClass = 'bg-green-100 text-green-800 border border-green-200';
      label = '✓ ';
    } else if (statusCode >= 400 && statusCode < 500) {
      colorClass = 'bg-yellow-100 text-yellow-800 border border-yellow-200';
      label = '⚠ ';
    } else if (statusCode >= 500) {
      colorClass = 'bg-red-100 text-red-800 border border-red-200';
      label = '✖ ';
    }
    
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-semibold ${colorClass}`}>
        {label}{statusCode}
      </span>
    );
  };

  const getMethodBadge = (method?: string) => {
    if (!method) return null;
    
    const methodColors: Record<string, string> = {
      'GET': 'bg-blue-100 text-blue-800',
      'POST': 'bg-green-100 text-green-800',
      'PUT': 'bg-yellow-100 text-yellow-800',
      'DELETE': 'bg-red-100 text-red-800',
      'PATCH': 'bg-purple-100 text-purple-800'
    };
    
    const colorClass = methodColors[method] || 'bg-gray-100 text-gray-800';
    
    return (
      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${colorClass}`}>
        {method}
      </span>
    );
  };

  const filteredLogs = logs.filter(log => {
    const matchesSearch = !filters.search || 
      log.message.toLowerCase().includes(filters.search.toLowerCase()) ||
      (log.endpoint && log.endpoint.toLowerCase().includes(filters.search.toLowerCase())) ||
      (log.username && log.username.toLowerCase().includes(filters.search.toLowerCase())) ||
      (log.action && log.action.toLowerCase().includes(filters.search.toLowerCase()));
    
    const matchesLevel = !filters.level || log.level === filters.level;
    
    const matchesEndpoint = !filters.endpoint || 
      (log.endpoint && log.endpoint.includes(filters.endpoint));
    
    // Time range filter
    if (filters.timeRange && filters.timeRange !== 'all') {
      const logDate = log.timestamp?._seconds 
        ? new Date(log.timestamp._seconds * 1000)
        : log.timestamp?.seconds 
        ? new Date(log.timestamp.seconds * 1000)
        : new Date(log.timestamp);
      
      const now = new Date();
      const diffHours = (now.getTime() - logDate.getTime()) / (1000 * 60 * 60);
      
      switch (filters.timeRange) {
        case '1h':
          if (diffHours > 1) return false;
          break;
        case '24h':
          if (diffHours > 24) return false;
          break;
        case '7d':
          if (diffHours > 168) return false;
          break;
      }
    }
    
    return matchesSearch && matchesLevel && matchesEndpoint;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-2xl mb-6 shadow-lg">
            <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-lg text-gray-600">Logs laden...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 py-12 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header Section */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-2xl mb-6 shadow-lg">
            <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <h1 className="text-4xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent mb-3">
            API Logs & Activity
          </h1>
          <p className="text-lg text-gray-600 mb-2">
            Real-time monitoring van alle API activiteit
          </p>
          <div className="flex justify-center items-center gap-4 text-sm text-gray-500">
            <span className="flex items-center gap-1">
              <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
              Live updates
            </span>
            <span>•</span>
            <span>{logs.length} logs geladen</span>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row justify-center items-center gap-4 mb-8">
          <button
            onClick={fetchLogs}
            className="w-full sm:w-auto bg-white hover:bg-gray-50 text-gray-700 px-6 py-3 rounded-xl font-semibold transition-all duration-200 flex items-center justify-center space-x-2 shadow-lg border border-gray-200 hover:shadow-xl"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            <span>Vernieuwen</span>
          </button>
          <button
            onClick={() => setShowClearModal(true)}
            className="w-full sm:w-auto bg-gradient-to-r from-red-600 to-pink-600 hover:from-red-700 hover:to-pink-700 text-white px-6 py-3 rounded-xl font-semibold transition-all duration-200 flex items-center justify-center space-x-2 shadow-lg hover:shadow-xl transform hover:scale-[1.02]"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
            <span>Logs Wissen</span>
          </button>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-2xl shadow-2xl p-6 border border-gray-100 relative overflow-hidden mb-8">
          <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-blue-100 to-indigo-200 rounded-full -translate-y-16 translate-x-16 opacity-20"></div>
          <div className="relative z-10">
            <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
              </svg>
              Filters
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Zoeken</label>
                <input
                  type="text"
                  value={filters.search}
                  onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                  placeholder="Zoek in logs..."
                  className="w-full border border-gray-300 rounded-xl px-3 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 shadow-sm hover:shadow-md"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Log Level</label>
                <select
                  value={filters.level}
                  onChange={(e) => setFilters(prev => ({ ...prev, level: e.target.value }))}
                  className="w-full border border-gray-300 rounded-xl px-3 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 shadow-sm hover:shadow-md"
                >
                  <option value="">Alle Levels</option>
                  <option value="error">Error</option>
                  <option value="warn">Warning</option>
                  <option value="info">Info</option>
                  <option value="debug">Debug</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Endpoint</label>
                <input
                  type="text"
                  value={filters.endpoint}
                  onChange={(e) => setFilters(prev => ({ ...prev, endpoint: e.target.value }))}
                  placeholder="Filter op endpoint..."
                  className="w-full border border-gray-300 rounded-xl px-3 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 shadow-sm hover:shadow-md"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Tijdsbereik</label>
                <select
                  value={filters.timeRange}
                  onChange={(e) => setFilters(prev => ({ ...prev, timeRange: e.target.value }))}
                  className="w-full border border-gray-300 rounded-xl px-3 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 shadow-sm hover:shadow-md"
                >
                  <option value="1h">Laatste uur</option>
                  <option value="24h">Laatste 24 uur</option>
                  <option value="7d">Laatste 7 dagen</option>
                  <option value="all">Alles</option>
                </select>
              </div>
            </div>
            <div className="mt-4 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
              <p className="text-sm text-gray-600">
                Toont {filteredLogs.length} van {logs.length} log entries
              </p>
              <button
                onClick={() => setFilters({ search: '', level: '', endpoint: '', timeRange: '24h' })}
                className="text-sm text-blue-600 hover:text-blue-800 font-medium transition-colors duration-200 flex items-center gap-1"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
                Reset Filters
              </button>
            </div>
          </div>
        </div>

        {/* Message */}
        {message && (
          <div className={`mb-6 p-4 rounded-xl border shadow-sm ${
            message.type === 'success' 
              ? 'bg-gradient-to-r from-green-50 to-emerald-50 border-green-200 text-green-800' 
              : 'bg-gradient-to-r from-red-50 to-pink-50 border-red-200 text-red-800'
          }`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <span className="mr-2">{message.type === 'success' ? '✅' : '❌'}</span>
                <span>{message.text}</span>
              </div>
              <button
                onClick={() => setMessage(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>
          </div>
        )}

        {error && (
          <div className="bg-gradient-to-r from-red-50 to-pink-50 border border-red-200 rounded-xl p-4 mb-6 shadow-sm">
            <div className="flex">
              <span className="text-red-400">❌</span>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">Fout</h3>
                <p className="text-sm text-red-700 mt-1">{error}</p>
              </div>
              <button
                onClick={() => setError(null)}
                className="ml-auto text-red-400 hover:text-red-600"
              >
                ✕
              </button>
            </div>
          </div>
        )}

        {/* Logs List */}
        <div className="space-y-4">
          {filteredLogs.length === 0 ? (
            <div className="bg-white rounded-2xl shadow-2xl p-12 border border-gray-100 text-center">
              <svg className="w-16 h-16 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Geen logs gevonden</h3>
              <p className="text-gray-600">Er zijn geen logs die overeenkomen met je filters</p>
            </div>
          ) : (
            filteredLogs.map((log) => (
              <div key={log.id} className="bg-white rounded-2xl shadow-2xl border border-gray-100 relative overflow-hidden hover:shadow-3xl transition-all duration-300">
                <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-br from-blue-100 to-indigo-200 rounded-full -translate-y-12 translate-x-12 opacity-20"></div>
                
                {/* Log Header */}
                <div className="p-6 pb-4 relative z-10">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      {/* Visual indicator bar */}
                      <div className={`absolute left-0 top-0 bottom-0 w-1 ${
                        log.level?.toLowerCase() === 'error' ? 'bg-red-500' :
                        log.level?.toLowerCase() === 'warn' || log.level?.toLowerCase() === 'warning' ? 'bg-yellow-500' :
                        log.level?.toLowerCase() === 'info' ? 'bg-blue-500' :
                        'bg-gray-400'
                      }`}></div>
                      
                      <div className="flex items-center flex-wrap gap-2 mb-3">
                        {getLevelBadge(log.level)}
                        {log.statusCode && getStatusCodeBadge(log.statusCode)}
                        {log.method && getMethodBadge(log.method)}
                        {log.action && getActionBadge(log.action)}
                      </div>
                      
                      <div className="flex items-center gap-4 text-sm text-gray-600 mb-2">
                        <span className="flex items-center gap-1">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          {formatDate(log.timestamp)}
                        </span>
                        {getRelativeTime(log.timestamp) && (
                          <span className="text-gray-500">({getRelativeTime(log.timestamp)})</span>
                        )}
                      </div>
                      
                      {log.endpoint && (
                        <p className="text-sm font-mono bg-gray-100 text-gray-700 px-3 py-1 rounded-lg inline-block mb-2">
                          {log.endpoint}
                        </p>
                      )}
                    </div>
                    
                    <button
                      onClick={() => toggleLogExpansion(log.id)}
                      className="ml-4 p-2 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                      <svg 
                        className={`w-5 h-5 text-gray-500 transform transition-transform ${expandedLogs.has(log.id) ? 'rotate-180' : ''}`} 
                        fill="none" 
                        stroke="currentColor" 
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                  </div>
                  
                  {/* Main Message with preview info */}
                  <div className="mt-3">
                    <p className="text-gray-900 font-medium">{log.message}</p>
                    
                    {/* Quick info preview */}
                    <div className="mt-2 flex flex-wrap gap-3 text-xs text-gray-600">
                      {log.username && (
                        <span className="flex items-center gap-1">
                          <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                          </svg>
                          {log.username}
                        </span>
                      )}
                      {log.licenseKey && (
                        <span className="flex items-center gap-1">
                          <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M18 8a6 6 0 01-7.743 5.743L10 14l-1 1-1 1H6v2H2v-4l4.257-4.257A6 6 0 1118 8zm-6-4a1 1 0 100 2 2 2 0 012 2 1 1 0 102 0 4 4 0 00-4-4z" clipRule="evenodd" />
                          </svg>
                          {log.licenseKey}
                        </span>
                      )}
                      {log.machineName && (
                        <span className="flex items-center gap-1">
                          <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M3 5a2 2 0 012-2h10a2 2 0 012 2v8a2 2 0 01-2 2h-2.22l.123.489.804.804A1 1 0 0113 18H7a1 1 0 01-.707-1.707l.804-.804L7.22 15H5a2 2 0 01-2-2V5zm5.771 7H5V5h10v7H8.771z" clipRule="evenodd" />
                          </svg>
                          {log.machineName}
                        </span>
                      )}
                      {log.duration !== undefined && (
                        <span className="flex items-center gap-1">
                          <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                          </svg>
                          {log.duration}ms
                        </span>
                      )}
                      {log.error && (
                        <span className="flex items-center gap-1 text-red-600">
                          <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                          </svg>
                          Error
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                
                {/* Expandable Details */}
                {expandedLogs.has(log.id) && (
                  <div className="border-t border-gray-100 bg-gray-50 p-6 pt-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* Left Column */}
                      <div className="space-y-3">
                        {(log.username || log.userId) && (
                          <div>
                            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Gebruiker</p>
                            <p className="text-sm text-gray-700 bg-white px-3 py-2 rounded-lg">
                              {log.username || log.userId}
                            </p>
                          </div>
                        )}
                        
                        {log.licenseKey && (
                          <div>
                            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Licentie Sleutel</p>
                            <p className="text-sm font-mono text-gray-700 bg-white px-3 py-2 rounded-lg">
                              {log.licenseKey}
                            </p>
                          </div>
                        )}
                        
                        {log.machineName && (
                          <div>
                            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Machine</p>
                            <p className="text-sm text-gray-700 bg-white px-3 py-2 rounded-lg">
                              {log.machineName}
                            </p>
                          </div>
                        )}
                        
                        {log.hardwareFingerprint && (
                          <div>
                            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Hardware ID</p>
                            <p className="text-sm font-mono text-gray-700 bg-white px-3 py-2 rounded-lg truncate">
                              {log.hardwareFingerprint}
                            </p>
                          </div>
                        )}
                      </div>
                      
                      {/* Right Column */}
                      <div className="space-y-3">
                        {log.ip && (
                          <div>
                            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">IP Adres</p>
                            <p className="text-sm font-mono text-gray-700 bg-white px-3 py-2 rounded-lg">
                              {log.ip}
                            </p>
                          </div>
                        )}
                        
                        {log.duration !== undefined && (
                          <div>
                            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Response Tijd</p>
                            <p className="text-sm text-gray-700 bg-white px-3 py-2 rounded-lg">
                              {log.duration}ms
                            </p>
                          </div>
                        )}
                        
                        {(log.pluginVersion || log.revitVersion) && (
                          <div>
                            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Versies</p>
                            <div className="bg-white px-3 py-2 rounded-lg">
                              {log.pluginVersion && (
                                <p className="text-sm text-gray-700">Plugin: {log.pluginVersion}</p>
                              )}
                              {log.revitVersion && (
                                <p className="text-sm text-gray-700">Revit: {log.revitVersion}</p>
                              )}
                            </div>
                          </div>
                        )}
                        
                        {log.userAgent && (
                          <div>
                            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">User Agent</p>
                            <p className="text-sm text-gray-700 bg-white px-3 py-2 rounded-lg break-all text-xs">
                              {log.userAgent}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                    
                    {/* Error Section */}
                    {log.error && (
                      <div className="mt-4">
                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Error Details</p>
                        <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                          <p className="text-sm text-red-800 font-mono">{log.error}</p>
                        </div>
                      </div>
                    )}
                    
                    {/* Stack Trace */}
                    {log.stack && (
                      <div className="mt-4">
                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Stack Trace</p>
                        <pre className="text-xs text-gray-700 font-mono bg-gray-800 text-gray-100 p-3 rounded-lg overflow-x-auto">
                          {log.stack}
                        </pre>
                      </div>
                    )}
                    
                    {/* Request/Response Data */}
                    {(log.requestData || log.responseData) && (
                      <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                        {log.requestData && (
                          <div>
                            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Request Data</p>
                            <pre className="text-xs text-gray-700 font-mono bg-blue-50 border border-blue-200 p-3 rounded-lg overflow-x-auto">
                              {JSON.stringify(log.requestData, null, 2)}
                            </pre>
                          </div>
                        )}
                        {log.responseData && (
                          <div>
                            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Response Data</p>
                            <pre className="text-xs text-gray-700 font-mono bg-green-50 border border-green-200 p-3 rounded-lg overflow-x-auto">
                              {JSON.stringify(log.responseData, null, 2)}
                            </pre>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
        
        {/* Load More Button */}
        {filteredLogs.length > 0 && (
          <div className="mt-8 text-center">
            <button
              onClick={fetchLogs}
              className="inline-flex items-center px-6 py-3 border border-gray-300 shadow-sm text-base font-medium rounded-xl text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              Meer logs laden
            </button>
          </div>
        )}
      </div>
      
      {/* Copyright Footer */}
      <div className="mt-12 text-center">
        <p className="text-sm text-gray-500">
          © 2025 S3e • Powered by Firebase
        </p>
      </div>

      {/* Clear Logs Modal */}
      {showClearModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-md shadow-lg rounded-2xl bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                <svg className="w-6 h-6 mr-2 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
                Logs Wissen
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                Weet je zeker dat je alle logs wilt wissen? Deze actie kan niet ongedaan worden gemaakt.
              </p>
              
              <div className="bg-red-50 border border-red-200 rounded-xl p-3 mb-4">
                <div className="flex items-start">
                  <svg className="w-5 h-5 text-red-400 mr-2 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  <div className="text-sm text-red-800">
                    <p className="font-semibold">Let op:</p>
                    <p className="mt-1">Alle log entries worden permanent verwijderd. Dit omvat:</p>
                    <ul className="mt-2 list-disc list-inside space-y-1">
                      <li>API call logs</li>
                      <li>Error logs</li>
                      <li>Debug informatie</li>
                      <li>Gebruikersactiviteit</li>
                    </ul>
                  </div>
                </div>
              </div>
              
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowClearModal(false)}
                  disabled={clearing}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800 disabled:opacity-50 transition-colors duration-200"
                >
                  Annuleren
                </button>
                <button
                  onClick={clearLogs}
                  disabled={clearing}
                  className="bg-gradient-to-r from-red-600 to-pink-600 hover:from-red-700 hover:to-pink-700 text-white px-6 py-2 rounded-xl font-semibold transition-all duration-200 disabled:opacity-50 flex items-center space-x-2 shadow-lg hover:shadow-xl"
                >
                  {clearing ? (
                    <>
                      <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <span>Wissen...</span>
                    </>
                  ) : (
                    <>
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                      <span>Logs Wissen</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Logs; 